/** @module csi/action/EmailController */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/when",
	"csi/_base/underscore",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/heading/Controller",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/resolver",
	"csi/state/rootStore",
	"csi/store/Cache",
	"csi/store/Names",
	"csi/utility/htmlUtility",
	"csi/widget/Dialog",
	"csi/widget/EmailForm",
	"csi/widget/MessageDialog",
	"csi/xml"
], function(declare, lang, when, _, comm, PostRequest, Controller, html, locale, reflection, resolver, { sessionStore }, Cache, Names, {purifyForHtml}, Dialog, EmailForm, MessageDialog, xml) {

	var Globals = {
		USERS_STORES_MODULE: "MessageProvider",
		USERS_STORES_OPERATION: "GetAvailableRecipients"
	};

	var TOOLTIP_STORE_ATTR = "Email";
	var EXPANDABLE_STORE_ATTR = "Expandable";

	var EmailController = declare(Controller, {
		// Boolean: allow arbitrary email addresses
		allowArbitraryEmailAddresses: true,

		// Dialog: dialog with EmailForm
		dialog: null,

		// Object: values from the dialog
		_values: null,

		// Cache: separate cache of items for "Fmt.Attr.List"
		emailCache: null,

		preProcess: function() {
			// summary: open email dialog
			var emailForm = new EmailForm();
			emailForm.allowArbitraryEmailAddresses = this.allowArbitraryEmailAddresses;
			var dlg = this.dialog = new Dialog({
				"class": "csiEmailDialog",
				processValues: true,
				destroyOnHide: true,
				dialogActions: true,
				form: emailForm,
				submitResource: "EmailButton",
				title: this.getTitle(),
				onSave: lang.hitch(this, this._onSave),
				onCancel: lang.hitch(this, this._onCancel)
			});

			dlg.startup();

			// set attributes and extension points
			var onExpand = lang.hitch(this, this._onExpandGroup);
			_.forEach(emailForm.getRecipientWidgets(), function(widget) {
				widget.tooltipAttr = TOOLTIP_STORE_ATTR;
				widget.expandableAttr = EXPANDABLE_STORE_ATTR;
				widget.onExpandGroup = onExpand;
			});

			// initialize the user/attachment store
			emailForm.setUsersStore(this.getUsersStore());
			var store = this.getAttachmentsStore();
			if (store)
				emailForm.setAttachmentsStore(store);

			// set initial values
			var self = this;
			when(this.getInitialValues(), function(initialValues) {
				dlg.set("value", initialValues);

				dlg.show();
				self.suspendProcess();
			});
		},

		_onSave: function(values) {
			// summary: sends the email
			this._values = values;

			// because value might contain all-email-users, replace it with the value from the field
			var recipientWidgets = this.dialog.form.getRecipientWidgets();

			this._values.toUsers = this._getRecipientsFieldValue(recipientWidgets[0]);
			this._values.ccUsers = this._getRecipientsFieldValue(recipientWidgets[1]);
			this._values.bccUsers = this._getRecipientsFieldValue(recipientWidgets[2]);

			this._validateValues(this._values);
		},

		_getRecipientsFieldValue: function(/*EmailFormListField*/ field) {
			return field.selectNode.get("value");
		},

		_validateValues: function(values) {
			this.resumeProcess();

			if (!values.subject) {
				Controller.prototype.confirmAction.call(this, {
					title: locale.resource("EmailSubjectEmpty_Title"),
					materialIcon: MessageDialog.ICON_WARNING,
					message: locale.resource("EmailSubjectEmpty_Confirm"),
					buttons: [MessageDialog.BUTTON_SEND_ANYWAY + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL],
					onResponse: lang.hitch(this, this._onConfirmSendEmptySubjectResponse, values)
				});
			} else {
				this.onValidateValues(values);
			}
		},

		_onConfirmSendEmptySubjectResponse: function(/*Object*/values, /*String*/buttonKey) {
			if (buttonKey == MessageDialog.BUTTON_SEND_ANYWAY) {
				this.onValidateValues(values);
			} else {
				this.suspendProcess();

				_.delay(lang.hitch(this, function() {
					this.dialog.form.subjectNode.focus();
					this.dialog.resumeAfterSave();
				}), 100);
			}
		},

		onValidateValues: function(values) {
			this.process();
		},

		_onCancel: function() {
			this.resumeProcess();
			this.postProcess();
		},

		toWriter: function() {
			var call = this.m_writer.callMethod("MessageProvider", "SendMessage");

			call.addParameter("SendTo", xml.ATTR_STRINGLIST, this._values.toUsers);
			call.addParameter("CCTo", xml.ATTR_STRINGLIST, this._values.ccUsers);
			call.addParameter("BCCTo", xml.ATTR_STRINGLIST, this._values.bccUsers);

			call.addParameter("Subject", xml.ATTR_STRING, this._values.subject);

			call.addParameter("Text", xml.ATTR_STRING, this.prepareMessageBody());

			return call;
		},

		postProcess: function(/*Boolean*/bStatus, /*Boolean*/bRepeat) {
			if (this.dialog) {
				if (bStatus === false) {
					bRepeat = true;
					this.dialog.resumeAfterSave();
				} else {
					this.dialog.hide();
				}
			}

			this.inherited(arguments, [bStatus, bRepeat]);
		},

		/**
		 * Creates separate cache for email items for "Fmt.Attr.List"
		 * @returns {Cache}
		 */
		getEmailCache: function() {
			if (!this.emailCache) {
				this.emailCache = new Cache();
			}
			return this.emailCache;
		},

		getUsersStore: function() {
			// summary: returns the users' store
			var options = this.getUsersStoreOptions();
			return new Names(options);
		},

		getUsersStoreOptions: function() {
			// summary: returns options to be passed to users' store constructor
			var companyInfo = resolver.resolveNode(reflection.URL_COMPANY);
			var recipientsRequest = new PostRequest(Globals.USERS_STORES_MODULE, Globals.USERS_STORES_OPERATION);
			if (!this.allowArbitraryEmailAddresses || !companyInfo.AllowContactLists) { //ContactLists are always allowed except for when emailing a link to a system page, since the link won't be accessible except for user in the system
				recipientsRequest.addParameter("DisallowContactLists", true);
			}
			recipientsRequest.addParameter("Fmt.Attr.List", "User:", true);
			recipientsRequest.addParameter("Fmt.Attr.List", "Group:", true);
			recipientsRequest.addParameter("Fmt.Attr.List", "ContactList:", true);
			recipientsRequest.addParameter("Fmt.Attr.List", "Contact:Email", true);
			var options = {
				bSort: false,
				customOperation: true,
				request: recipientsRequest,
				_processResultNode: lang.hitch(this, this._processResultNode)
			};
			return options;
		},

		_processResultNode: function(/*Node*/node, /*Cache*/cache) {
			// summary: convert a node into an item for 'Names' store
			this.getEmailCache().cacheNode(node);

			var name = locale.getNodeName(node, cache);
			var emailAddress, cssClass, expandable = false;
			switch (node.$Type) {
				case "User":
					cssClass = "emailUser";
					break;

				case "Group":
					expandable = true;
					cssClass = "emailGroup";
					break;

				case "ContactList":
					expandable = true;
					cssClass = "emailGroup";
					break;

				default:	// AbstractContact
					emailAddress = node.Email;
					cssClass = "emailOther";
					break;
			}

			var item = {
				$: node.$URL,
				Name: name,
				Label: ('<span class="' + cssClass + '">' + _.escape(name) + '</span>')
			};
			item[TOOLTIP_STORE_ATTR] = emailAddress;
			item[EXPANDABLE_STORE_ATTR] = expandable;

			return item;
		},

		getAttachmentsStore: function() {
			// summary: returns the attachments' store
			return null;
		},

		getInitialValues: function() {
			// summary: get initial values for Email form
			var initialValues = {message: ""};

			const {user, userDetails} = sessionStore;
			if (user) {
				if (user.SendSelfBCCEmail) {
					initialValues.bccUsers = [user.$URL];
				}

				if (userDetails.EmailSignature) {
					initialValues.message = "<br/>" + purifyForHtml(userDetails.EmailSignature);
				}
			}

			return initialValues;
		},

		expandGroup: function(/*EmailFormListField*/widget, /*String*/url) {
			let cache = this.getEmailCache();
			var group = cache.getNode(url);
			if (group.$Type == "Group") {
				var assignment = cache.getChildNode(group, "Assignment", true);
				widget.addItemsByUrls(assignment.__Members__);
			} else { //ContactList
				var contactURLs = group.Contacts;
				var dfd = cache.addRequestDeferred(comm.getNodeRequest(contactURLs));
				var self = this;

				this.when(dfd, function(response) {
					var contactNodes = cache.getNodes(contactURLs);

					_.forEach(contactNodes, function(contact) {
						var item = self._processResultNode(contact);
						widget._addItem(item);
					});
				});
			}

			widget.removeItemsByUrls([url]);
		},

		/**
		 * We might want to involve something like "css to inline" modules instead of using following function, which only address table borders
		 * https://www.npmjs.com/package/computed-style-to-inline-style
		 * https://github.com/lukehorvat/computed-style-to-inline-style/blob/master/index.js
		 * @param {string} value
		 * @return {sting}
		 */
		applyTableStyle: function(value) {
			// TODO: None of Froala CSS classes actually being included into email
			var wrapper = document.createElement('div');
			wrapper.innerHTML = value;
			var borderStyle = $(".fr-dashed-borders", wrapper).length ? "dashed" : "solid";
			var jTable = $("table", wrapper);
			jTable.css("border-collapse", "collapse");
			jTable.find("td,th").css("border", "1px " + borderStyle + " #dadada");
			return wrapper.innerHTML;
		},

		prepareMessageBody: function(value) {
			var sBody = '<html xmlns="http://www.w3.org/1999/xhtml"><head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8"/></head><body>';
			if (!value)
				// no value specified, so use internally specified message
				value = this._values.message;

			value = this.applyTableStyle(value);

			sBody += purifyForHtml(value);
			sBody += '</body></html>';
			return sBody;
		},

		_onExpandGroup: function(/*EmailFormListField*/widget, /*String*/url) {
			this.confirmAction({
				title: locale.resource("EmailExpandGroup_Prompt"),
				message: locale.resource("EmailExpandGroup_Confirm"),
				buttons: [MessageDialog.BUTTON_EXPAND_ANYWAY + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL],
				onResponse: lang.hitch(this, this._onExpandGroupResponse, widget, url)
			});
		},

		_onExpandGroupResponse: function(/*EmailFormListField*/widget, /*String*/url, /*String*/buttonKey) {
			if (buttonKey == BUTTON_EXPAND_ANYWAY)
				this.expandGroup(widget, url);
		}
	});

	// mixin globals
	return lang.mixin(EmailController, Globals);
});